import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMovieDetails } from '../hooks/useMovies'
import { useWatchedMovies, useWatchlist } from '../hooks/useFirestore'
import { useFriends } from '../hooks/useFriends'
import { getImageUrl, getWatchProviders, getMovieVideos } from '../services/tmdb'
import { checkMovieInJellyfin, getStreamUrl, getDirectStreamUrl } from '../services/jellyfin'
import { getTorrentStreams } from '../services/torrentio'
import { requestMovie, getMovieRequest } from '../services/movieRequests'
import Rating from '../components/common/Rating'
import Modal from '../components/common/Modal'
import Loader from '../components/common/Loader'
import VideoPlayer from '../components/common/VideoPlayer'
import StreamSelector from '../components/common/StreamSelector'
import DebridPlayer from '../components/common/DebridPlayer'
import './MovieDetail.css'

// Provider URLs mapping (provider_id -> website)
const PROVIDER_URLS = {
  8: 'https://www.netflix.com',           // Netflix
  9: 'https://www.primevideo.com',        // Amazon Prime Video
  119: 'https://www.primevideo.com',      // Amazon Prime Video
  10: 'https://www.primevideo.com',       // Amazon Video
  337: 'https://www.disneyplus.com',      // Disney+
  384: 'https://www.max.com',             // HBO Max
  1899: 'https://www.max.com',            // Max
  2: 'https://tv.apple.com',              // Apple TV
  3: 'https://play.google.com/store/movies', // Google Play Movies
  35: 'https://www.rakuten.tv',           // Rakuten TV
  63: 'https://www.filmin.es',            // Filmin
  149: 'https://www.movistarplus.es',     // Movistar Plus
  64: 'https://www.movistarplus.es',      // Movistar Plus+
  350: 'https://www.apple.com/apple-tv-plus/', // Apple TV+
  531: 'https://www.paramountplus.com',   // Paramount+
  283: 'https://www.crunchyroll.com',     // Crunchyroll
  521: 'https://www.skyshowtime.com',     // SkyShowtime
  1773: 'https://www.skyshowtime.com',    // SkyShowtime
}

const getProviderUrl = (provider, fallbackLink) => {
  return PROVIDER_URLS[provider.provider_id] || fallbackLink
}

function MovieDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { movie, loading, error } = useMovieDetails(id)
  const { addMovie: addToWatched, addViewing, checkIfWatched, getMovieViewings, removeViewing } = useWatchedMovies()
  const { addMovie: addToWatchlist, checkIfInWatchlist, removeMovie: removeFromWatchlist } = useWatchlist()
  const { friends } = useFriends()

  const [watchedData, setWatchedData] = useState(null)
  const [viewings, setViewings] = useState([])
  const [inWatchlist, setInWatchlist] = useState(null)
  const [watchProviders, setWatchProviders] = useState(null)
  const [originalVideos, setOriginalVideos] = useState([])
  const [jellyfinData, setJellyfinData] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showWatchlistModal, setShowWatchlistModal] = useState(false)
  const [selectedFriends, setSelectedFriends] = useState([])
  const [selectedWatchlistFriends, setSelectedWatchlistFriends] = useState([])
  const [watchData, setWatchData] = useState({
    watchedDate: new Date().toISOString().split('T')[0],
    rating: 0,
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [savingWatchlist, setSavingWatchlist] = useState(false)
  const [deletingViewing, setDeletingViewing] = useState(null)
  const [showVideoPlayer, setShowVideoPlayer] = useState(false)
  const [torrentStreams, setTorrentStreams] = useState([])
  const [showStreamSelector, setShowStreamSelector] = useState(false)
  const [selectedTorrent, setSelectedTorrent] = useState(null)
  const [showTorrentPlayer, setShowTorrentPlayer] = useState(false)
  
  // Movie request state
  const [movieRequest, setMovieRequest] = useState(null)
  const [requestingMovie, setRequestingMovie] = useState(false)
  const [requestSuccess, setRequestSuccess] = useState(false)

  useEffect(() => {
    const checkStatus = async () => {
      const watchedDoc = await checkIfWatched(parseInt(id))
      setWatchedData(watchedDoc)
      
      if (watchedDoc) {
        const movieViewings = await getMovieViewings(parseInt(id))
        setViewings(movieViewings)
      }
      
      const watchlistDoc = await checkIfInWatchlist(parseInt(id))
      setInWatchlist(watchlistDoc)
    }
    if (id) checkStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    const fetchProviders = async () => {
      const providers = await getWatchProviders(id)
      setWatchProviders(providers)
    }
    if (id) fetchProviders()
  }, [id])

  useEffect(() => {
    const fetchVideos = async () => {
      const videos = await getMovieVideos(id)
      setOriginalVideos(videos)
    }
    if (id) fetchVideos()
  }, [id])

  useEffect(() => {
    const checkJellyfin = async () => {
      if (!movie) return
      const result = await checkMovieInJellyfin(movie.id)
      setJellyfinData(result)
      
      // Check if movie has been requested
      if (movie.imdb_id) {
        const existingRequest = await getMovieRequest(movie.imdb_id)
        setMovieRequest(existingRequest)
        
        // If movie is now in Jellyfin and was requested, we could auto-delete
        // But this is handled by a separate cleanup process
      }
    }
    if (movie) checkJellyfin()
  }, [movie])

  useEffect(() => {
    // Clear previous streams when movie changes
    setTorrentStreams([])
    setSelectedTorrent(null)
    
    const fetchStreams = async () => {
      if (!movie?.imdb_id) {
        console.log('No IMDB ID for movie:', movie?.title)
        return
      }
      console.log('Fetching streams for:', movie.title, 'IMDB:', movie.imdb_id)
      const streams = await getTorrentStreams(movie.imdb_id)
      console.log('Got streams:', streams.length, streams.slice(0, 3).map(s => s.title))
      setTorrentStreams(streams)
    }
    if (movie) fetchStreams()
  }, [movie])

  const toggleFriend = (friend) => {
    setSelectedFriends(prev => {
      const exists = prev.find(f => f.friendId === friend.friendId)
      if (exists) {
        return prev.filter(f => f.friendId !== friend.friendId)
      }
      return [...prev, { id: friend.friendId, name: friend.friendName }]
    })
  }

  const handleRequestMovie = async () => {
    if (!movie?.imdb_id) return
    
    setRequestingMovie(true)
    try {
      const result = await requestMovie(movie.imdb_id, movie)
      setMovieRequest(result)
      setRequestSuccess(true)
      setTimeout(() => setRequestSuccess(false), 3000)
    } catch (error) {
      console.error('Error requesting movie:', error)
      alert(error.message || 'Error al solicitar la película')
    } finally {
      setRequestingMovie(false)
    }
  }

  const handleMarkAsWatched = async () => {
    if (!movie) return
    setSaving(true)
    try {
      const viewing = {
        date: watchData.watchedDate,
        rating: watchData.rating,
        notes: watchData.notes,
        watchedWith: selectedFriends
      }

      if (watchedData) {
        // Add new viewing to existing movie
        await addViewing(watchedData.id, viewing, selectedFriends)
      } else {
        // First time watching
        const movieData = {
          tmdbId: movie.id,
          title: movie.title,
          posterPath: movie.poster_path,
          releaseDate: movie.release_date,
          genres: movie.genres?.map(g => g.name) || [],
          runtime: movie.runtime || 0
        }
        await addToWatched(movieData, viewing, selectedFriends)
      }

      // Remove from watchlist if it was there
      if (inWatchlist) {
        await removeFromWatchlist(inWatchlist.id)
        setInWatchlist(null)
      }

      setShowModal(false)
      setWatchedData(true)
      
      // Reset form
      setSelectedFriends([])
      setWatchData({
        watchedDate: new Date().toISOString().split('T')[0],
        rating: 0,
        notes: ''
      })
      
      // Refresh viewings
      const movieViewings = await getMovieViewings(parseInt(id))
      setViewings(movieViewings)
    } catch (err) {
      console.error('Error marking as watched:', err)
    } finally {
      setSaving(false)
    }
  }

  const toggleWatchlistFriend = (friend) => {
    setSelectedWatchlistFriends(prev => {
      const exists = prev.find(f => f.id === friend.friendId)
      if (exists) {
        return prev.filter(f => f.id !== friend.friendId)
      }
      return [...prev, { id: friend.friendId, name: friend.friendName }]
    })
  }

  const handleAddToWatchlist = async () => {
    if (!movie) return
    setSavingWatchlist(true)
    try {
      const movieData = {
        tmdbId: movie.id,
        title: movie.title,
        posterPath: movie.poster_path,
        releaseDate: movie.release_date,
        genres: movie.genres?.map(g => g.name) || []
      }
      await addToWatchlist(movieData, selectedWatchlistFriends)
      setInWatchlist(true)
      setShowWatchlistModal(false)
      setSelectedWatchlistFriends([])
    } catch (err) {
      console.error('Error adding to watchlist:', err)
    } finally {
      setSavingWatchlist(false)
    }
  }

  const handleDeleteViewing = async (viewing) => {
    setDeletingViewing(viewing.id)
    try {
      const result = await removeViewing(viewing.id, viewing.movieDocId)
      if (result.movieDeleted) {
        setWatchedData(null)
        setViewings([])
      } else {
        const movieViewings = await getMovieViewings(parseInt(id))
        setViewings(movieViewings)
        const watchedDoc = await checkIfWatched(parseInt(id))
        setWatchedData(watchedDoc)
      }
    } catch (err) {
      console.error('Error deleting viewing:', err)
    } finally {
      setDeletingViewing(null)
    }
  }

  if (loading) return <Loader fullScreen />
  if (error) return <div className="page container"><p>Error: {error}</p></div>
  if (!movie) return <div className="page container"><p>Película no encontrada</p></div>

  const year = movie.release_date?.substring(0, 4)
  const runtime = movie.runtime ? `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m` : null
  const backdrop = getImageUrl(movie.backdrop_path, 'original')
  const poster = getImageUrl(movie.poster_path, 'w500')

  return (
    <div className="movie-detail-page">
      {backdrop && (
        <div className="movie-backdrop" style={{ backgroundImage: `url(${backdrop})` }} />
      )}

      <div className="container movie-detail-content">
        <button className="back-btn btn btn-ghost" onClick={() => navigate(-1)}>
          ← Volver
        </button>

        <div className="movie-detail-grid">
          <div className="movie-poster-section">
            {poster ? (
              <img src={poster} alt={movie.title} className="movie-detail-poster" />
            ) : (
              <div className="movie-detail-poster-placeholder">🎬</div>
            )}
          </div>

          <div className="movie-info-section">
            <h1 className="movie-detail-title">{movie.title}</h1>
            
            <div className="movie-meta">
              {year && <span className="meta-item">{year}</span>}
              {runtime && <span className="meta-item">{runtime}</span>}
              {movie.vote_average > 0 && (
                <span className="meta-item tmdb-rating">
                  ⭐ {movie.vote_average.toFixed(1)}/10
                </span>
              )}
            </div>

            {movie.genres?.length > 0 && (
              <div className="movie-genres">
                {movie.genres.map(genre => (
                  <span key={genre.id} className="badge">{genre.name}</span>
                ))}
              </div>
            )}

            {movie.overview && (
              <div className="movie-overview">
                <h3>Sinopsis</h3>
                <p>{movie.overview}</p>
              </div>
            )}

            {/* Jellyfin - Private Server */}
            {jellyfinData?.found && (
              <div className="jellyfin-section">
                <div className="jellyfin-available">
                  <span className="jellyfin-icon">🟣</span>
                  <span className="jellyfin-text">Disponible en Pedrazo TV</span>
                  <div className="jellyfin-buttons">
                    <a 
                      href={jellyfinData.playUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-jellyfin-secondary"
                    >
                      Ir a Pedrazo TV
                    </a>
                    <button 
                      className="btn btn-jellyfin"
                      onClick={() => setShowVideoPlayer(true)}
                    >
                      ▶ Ver
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Movie Request - When not in Jellyfin */}
            {jellyfinData && !jellyfinData.found && movie?.imdb_id && (
              <div className="jellyfin-section">
                <div className="jellyfin-unavailable">
                  <span className="jellyfin-icon">🟣</span>
                  <span className="jellyfin-text">Esta película aún no está disponible en Pedrazo TV</span>
                  <div className="jellyfin-buttons">
                    {movieRequest || requestSuccess ? (
                      <span className="request-status">Ya solicitada</span>
                    ) : (
                      <button 
                        className="btn btn-jellyfin"
                        onClick={handleRequestMovie}
                        disabled={requestingMovie}
                      >
                        {requestingMovie ? 'Solicitando...' : 'Solicitar'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Torrent Streaming */}
            {torrentStreams.length > 0 && (
              <div className="torrent-section">
                <div className="torrent-available">
                  <span className="torrent-icon">🔥</span>
                  <span className="torrent-text">{torrentStreams.length} fuentes disponibles</span>
                  <button 
                    className="btn btn-torrent"
                    onClick={() => setShowStreamSelector(true)}
                  >
                    ▶ Ver ahora
                  </button>
                </div>
              </div>
            )}

            {/* Watch Providers */}
            {watchProviders && (watchProviders.flatrate || watchProviders.rent || watchProviders.buy) && (
              <div className="watch-providers">
                <h3>📺 Dónde ver</h3>
                
                {watchProviders.flatrate && (
                  <div className="provider-section">
                    <span className="provider-label">Streaming:</span>
                    <div className="provider-list">
                      {watchProviders.flatrate.map(provider => (
                        <a 
                          key={provider.provider_id} 
                          href={getProviderUrl(provider, watchProviders.link)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="provider-item"
                          title={provider.provider_name}
                        >
                          <img 
                            src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`} 
                            alt={provider.provider_name}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {watchProviders.rent && (
                  <div className="provider-section">
                    <span className="provider-label">Alquilar:</span>
                    <div className="provider-list">
                      {watchProviders.rent.map(provider => (
                        <a 
                          key={provider.provider_id} 
                          href={getProviderUrl(provider, watchProviders.link)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="provider-item"
                          title={provider.provider_name}
                        >
                          <img 
                            src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`} 
                            alt={provider.provider_name}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {watchProviders.buy && (
                  <div className="provider-section">
                    <span className="provider-label">Comprar:</span>
                    <div className="provider-list">
                      {watchProviders.buy.map(provider => (
                        <a 
                          key={provider.provider_id} 
                          href={getProviderUrl(provider, watchProviders.link)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="provider-item"
                          title={provider.provider_name}
                        >
                          <img 
                            src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`} 
                            alt={provider.provider_name}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <p className="provider-attribution">
                  Datos de <a href="https://www.justwatch.com" target="_blank" rel="noopener noreferrer">JustWatch</a>
                </p>
              </div>
            )}

            <div className="movie-actions">
              <button className="btn btn-primary btn-lg" onClick={() => setShowModal(true)}>
                {watchedData ? '+ Añadir otra vista' : '✓ Marcar como vista'}
              </button>

              {!watchedData && !inWatchlist && (
                <button className="btn btn-secondary btn-lg" onClick={() => setShowWatchlistModal(true)}>
                  🎯 Ver más tarde
                </button>
              )}

              {inWatchlist && !watchedData && (
                <div className="watchlist-badge-container">
                  <div className="watchlist-badge">
                    🎯 En tu lista
                  </div>
                  {inWatchlist.recommendedBy && (
                    <div className="watchlist-info">
                      Recomendada por: <strong>{inWatchlist.recommendedBy.name}</strong>
                    </div>
                  )}
                  {!inWatchlist.recommendedBy && inWatchlist.watchWith?.length > 0 && (
                    <div className="watchlist-info">
                      Ver con: <strong>{inWatchlist.watchWith.map(f => f.name).join(', ')}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Viewing History */}
            {viewings.length > 0 && (
              <div className="viewing-history">
                <h3>📋 Tu historial de vistas</h3>
                <div className="viewings-list">
                  {viewings.map((viewing, index) => (
                    <div key={viewing.id || index} className="viewing-item">
                      <div className="viewing-content">
                        <div className="viewing-date">
                          📅 {new Date(viewing.date).toLocaleDateString('es-ES', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </div>
                        {viewing.rating > 0 && (
                          <div className="viewing-rating">
                            {'⭐'.repeat(viewing.rating)}
                          </div>
                        )}
                        {viewing.watchedWith?.length > 0 && (
                          <div className="viewing-friends">
                            👥 Con: {viewing.watchedWith.map(f => f.name).join(', ')}
                          </div>
                        )}
                        {viewing.notes && (
                          <div className="viewing-notes">💭 {viewing.notes}</div>
                        )}
                      </div>
                      <button 
                        className="viewing-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteViewing(viewing)
                        }}
                        disabled={deletingViewing === viewing.id}
                        title="Eliminar visualización"
                      >
                        {deletingViewing === viewing.id ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trailer - moved after viewing history */}
            {originalVideos.length > 0 && (() => {
              // Prioritize: English trailer > any trailer > any YouTube video
              const trailer = originalVideos.find(
                v => v.type === 'Trailer' && v.site === 'YouTube' && v.iso_639_1 === 'en'
              ) || originalVideos.find(
                v => v.type === 'Trailer' && v.site === 'YouTube'
              ) || originalVideos.find(v => v.site === 'YouTube')
              
              return trailer ? (
                <div className="movie-trailer">
                  <h3>🎬 Trailer</h3>
                  <div className="trailer-container">
                    <iframe
                      src={`https://www.youtube.com/embed/${trailer.key}`}
                      title={trailer.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              ) : null
            })()}

            {movie.credits?.cast?.length > 0 && (
              <div className="movie-cast">
                <h3>Reparto</h3>
                <div className="cast-list">
                  {movie.credits.cast.slice(0, 6).map(actor => (
                    <div key={actor.id} className="cast-item">
                      {actor.profile_path ? (
                        <img 
                          src={getImageUrl(actor.profile_path, 'w185')} 
                          alt={actor.name}
                          className="cast-photo"
                        />
                      ) : (
                        <div className="cast-photo-placeholder">👤</div>
                      )}
                      <span className="cast-name">{actor.name}</span>
                      <span className="cast-character">{actor.character}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={watchedData ? 'Añadir nueva vista' : 'Marcar como vista'}
      >
        <div className="watch-form">
          <div className="form-group">
            <label>Fecha de visualización</label>
            <input
              type="date"
              className="input"
              value={watchData.watchedDate}
              onChange={(e) => setWatchData(prev => ({ ...prev, watchedDate: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Tu puntuación</label>
            <Rating
              value={watchData.rating}
              onChange={(rating) => setWatchData(prev => ({ ...prev, rating }))}
              size="lg"
            />
          </div>

          <div className="form-group">
            <label>¿Con quién la viste?</label>
            {friends.length > 0 ? (
              <div className="friends-selector">
                {friends.map(friend => (
                  <button
                    key={friend.id}
                    type="button"
                    className={`friend-chip ${selectedFriends.find(f => f.id === friend.friendId) ? 'selected' : ''}`}
                    onClick={() => toggleFriend(friend)}
                  >
                    {friend.friendName}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted">
                No tienes amigos agregados. <a href="/friends" className="link">Agregar amigos</a>
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Notas (opcional)</label>
            <textarea
              className="input textarea"
              placeholder="Tus pensamientos sobre la película..."
              value={watchData.notes}
              onChange={(e) => setWatchData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          <button 
            className="btn btn-primary btn-lg" 
            onClick={handleMarkAsWatched}
            disabled={saving}
            style={{ width: '100%' }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal>

      {/* Watchlist Modal */}
      <Modal
        isOpen={showWatchlistModal}
        onClose={() => setShowWatchlistModal(false)}
        title="Añadir a Ver más tarde"
      >
        <div className="watch-form">
          <div className="form-group">
            <label>¿Con quién quieres verla?</label>
            <p className="text-sm text-muted" style={{ marginBottom: '0.5rem' }}>
              Si seleccionas amigos, la película aparecerá en su lista de "Ver más tarde"
            </p>
            {friends.length > 0 ? (
              <div className="friends-selector">
                {friends.map(friend => (
                  <button
                    key={friend.id}
                    type="button"
                    className={`friend-chip ${selectedWatchlistFriends.find(f => f.id === friend.friendId) ? 'selected' : ''}`}
                    onClick={() => toggleWatchlistFriend(friend)}
                  >
                    {friend.friendName}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted">
                No tienes amigos agregados. <a href="/friends" className="link">Agregar amigos</a>
              </p>
            )}
          </div>

          <button 
            className="btn btn-primary btn-lg" 
            onClick={handleAddToWatchlist}
            disabled={savingWatchlist}
            style={{ width: '100%' }}
          >
            {savingWatchlist ? 'Guardando...' : 'Añadir a mi lista'}
          </button>
        </div>
      </Modal>

      {/* Video Player */}
      {showVideoPlayer && jellyfinData?.found && (
        <VideoPlayer
          streamUrl={getStreamUrl(jellyfinData.item.id)}
          directUrl={getDirectStreamUrl(jellyfinData.item.id)}
          title={movie.title}
          onClose={() => setShowVideoPlayer(false)}
        />
      )}

      {/* Stream Selector */}
      {showStreamSelector && (
        <StreamSelector
          streams={torrentStreams}
          title={movie.title}
          onSelect={(stream) => {
            setSelectedTorrent(stream)
            setShowStreamSelector(false)
            setShowTorrentPlayer(true)
          }}
          onClose={() => setShowStreamSelector(false)}
        />
      )}

      {/* Debrid Player */}
      {showTorrentPlayer && selectedTorrent && (
        <DebridPlayer
          magnetUrl={selectedTorrent.magnetUrl}
          title={movie.title}
          imdbId={movie.imdb_id}
          movieData={{
            tmdbId: movie.id,
            title: movie.title,
            posterPath: movie.poster_path,
            releaseDate: movie.release_date,
            genres: movie.genres?.map(g => g.name) || [],
            runtime: movie.runtime || 0,
          }}
          onClose={() => {
            setShowTorrentPlayer(false)
            setSelectedTorrent(null)
          }}
        />
      )}
    </div>
  )
}

export default MovieDetail
